import * as React from "react"

import { Badge, Skeleton } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { useFinanceFeatureQuery } from "@/features/finance/services/finance-query"
import { useWidgetMenu } from "@ibada/dashboard-engine"

import { WidgetBoundary } from "@/features/finance/overview/components/widget-boundary"
import { useFinanceOverviewScope } from "@/features/finance/overview/context/finance-overview-context"
import { OverviewRoutes, useOverviewNav } from "@/features/finance/overview/lib/overview-nav"
import type { MoneyText } from "@/features/finance/model/finance-types"
import { FinanceCategoryScope } from "@/features/finance/model/finance-types"
import {
  compareWatchlistAmountsDesc,
  formatWatchlistAmount,
  isZeroWatchlistAmount,
} from "@/features/finance/overview/lib/watchlist-display"
import {
  useWidgetCreateActions,
  type WidgetCreateKey,
} from "@/features/finance/overview/lib/use-widget-create-actions"
import {
  ConfigCheckList,
  ConfigSwitchRow,
  WidgetConfigDrawer,
  useWidgetConfig,
} from "@/features/finance/overview/components/widget-config-drawer"

const WIDGET_CREATE_KEYS: readonly WidgetCreateKey[] = ["income", "expense"]

/** How many funds to show by default (before the user picks a specific set). */
const DEFAULT_ROWS = 6

interface FundWatchlistConfig {
  /** null => show the top funds by balance; an array => show exactly these funds. */
  selectedIds: string[] | null
  hideZero: boolean
}

interface FundRow {
  id: string
  name: string
  /** Money: the canonical decimal string from the API. Compared with `compareMoney`, never with `a - b`. */
  balance: MoneyText
  currency: string
  isRestricted: boolean
}

/**
 * Fund Watchlist — the current position of funds. Shows each fund's current (available) balance and its
 * restricted/unrestricted status. Current-state (period-independent). Per-fund received/spent within the
 * selected period is deferred pending a date-scoped funds endpoint.
 */
export function FundWatchlistWidget() {
  const t = useTranslations("finance.overview")
  const scope = useFinanceOverviewScope()
  const go = useOverviewNav()
  const { useFinanceFundsQuery } = useFinanceFeatureQuery()
  const cfg = useWidgetConfig<FundWatchlistConfig>({ selectedIds: null, hideZero: false })

  const query = useFinanceFundsQuery(
    scope.workspaceId
      ? {
          workspaceId: scope.workspaceId,
          branchId: scope.branchId,
          // The system (parish) branch owns its funds under PARISH scope; a BRANCH scope targeting it is
          // rejected by the backend. Mirror the transaction form's context→scope mapping.
          scope: scope.context === "parish" ? FinanceCategoryScope.Parish : FinanceCategoryScope.Branch,
          limit: 100,
        }
      : null,
  )

  const rows = React.useMemo<FundRow[]>(() => {
    if (!query.data) return []
    const selected = cfg.config.selectedIds
    const mapped = query.data.items
      .map((fund) => ({
        id: fund.id,
        name: fund.name,
        balance: fund.balance,
        currency: fund.currency ?? scope.currency,
        isRestricted: fund.isRestricted,
      }))
      .filter((fund) => !cfg.config.hideZero || !isZeroWatchlistAmount(fund.balance, fund.currency))
      // Descending by balance, compared on exact minor units. `b.balance - a.balance` would coerce both sides to
      // floats, and two amounts that differ by a cent at the top of the range can compare EQUAL as floats — so
      // the watchlist could order the biggest funds wrongly.
      .sort(compareWatchlistAmountsDesc)
    return selected === null ? mapped.slice(0, DEFAULT_ROWS) : mapped.filter((fund) => selected.includes(fund.id))
  }, [query.data, cfg.config.hideZero, cfg.config.selectedIds, scope.currency])

  const create = useWidgetCreateActions(WIDGET_CREATE_KEYS)
  useWidgetMenu({
    configure: cfg.openDrawer,
    refresh: () => void query.refetch(),
    viewDetails: go.canNavigateTo(OverviewRoutes.funds) ? () => go(OverviewRoutes.funds) : undefined,
    create,
  })

  return (
    <>
    <WidgetBoundary
      isLoading={query.isLoading}
      isError={query.isError}
      isFetching={query.isFetching}
      data={query.data ? rows : undefined}
      isEmpty={(r) => r.length === 0}
      skeleton={<RowsSkeleton />}
      errorTitle={t("widgets.fundWatchlist.error")}
      onRetry={() => void query.refetch()}
      retryLabel={t("widgets.common.retry")}
      emptyTitle={t("widgets.fundWatchlist.empty.title")}
    >
      {(fundRows) => (
        <ul className="divide-y divide-border/60">
          {fundRows.map((fund) => (
            <li key={fund.id}>
              <button
                type="button"
                onClick={go.canNavigateTo(OverviewRoutes.funds) ? () => go(OverviewRoutes.funds) : undefined}
                disabled={!go.canNavigateTo(OverviewRoutes.funds)}
                className="group flex w-full items-center justify-between gap-3 py-1.5 text-left"
              >
                <span className="flex min-w-0 flex-1 items-center gap-1.5">
                  <span className="truncate text-sm">{fund.name}</span>
                  {fund.isRestricted ? (
                    <Badge variant="outline" className="shrink-0 px-1.5 py-0 text-[10px]">
                      {t("widgets.fundWatchlist.restricted")}
                    </Badge>
                  ) : null}
                </span>
                <span className="shrink-0 text-sm font-medium tabular-nums">
                  {formatWatchlistAmount(fund.balance, fund.currency)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </WidgetBoundary>

    <WidgetConfigDrawer
      open={cfg.open}
      title={t("widgets.fundWatchlist.title")}
      onSave={cfg.save}
      onCancel={cfg.cancel}
    >
      <ConfigCheckList
        label={t("widgets.fundWatchlist.config.funds")}
        emptyLabel={t("widgets.fundWatchlist.empty.title")}
        items={(query.data?.items ?? []).map((fund) => ({ id: fund.id, label: fund.name }))}
        selectedIds={cfg.draft.selectedIds}
        onChange={(ids) => cfg.patch({ selectedIds: ids })}
      />
      <ConfigSwitchRow
        label={t("widgets.fundWatchlist.config.hideZero")}
        checked={cfg.draft.hideZero}
        onCheckedChange={(value) => cfg.patch({ hideZero: value })}
      />
    </WidgetConfigDrawer>
    </>
  )
}

function RowsSkeleton() {
  return (
    <div className="space-y-2.5 py-1">
      {[58, 70, 46, 64, 52].map((w, index) => (
        <div key={index} className="flex items-center justify-between gap-3">
          <Skeleton className="h-3.5" style={{ width: `${w}%` }} />
          <Skeleton className="h-3.5 w-16" />
        </div>
      ))}
    </div>
  )
}
